import React, { useState } from 'react';
import Button from '../common/Button';
import { CloseIcon } from '../common/icons';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * Step 1 of "Avslutt abonnement" (pages/account/SubscriptionSettings.js): an
 * optional "why are you leaving?" question shown before the unchanged confirm
 * step (ConfirmPopup), on the same modal chrome.
 *
 * Nothing here is required. "Fortsett" passes on whatever was given — a
 * reason, a text, both, or neither (null, the same as skipping); "Hopp over"
 * discards any selection and passes null. The parent sends the answer with
 * the cancel request only when it is non-null; the backend
 * (lib/cancelReason.js) drops anything invalid and never fails the cancel
 * over it.
 *
 *   onContinue(answer|null) — answer is { cancelReason?, cancelReasonText? }
 *   onSkip()                — proceed without any answer
 *   onClose()               — back out entirely (× or backdrop)
 */

export const CANCEL_REASONS = ['granted', 'too_expensive', 'unmet_need', 'other'];
export const REASON_TEXT_MAX = 500;
// The remaining-characters line only appears past this length.
const REASON_TEXT_COUNTER_FROM = 400;

const REASON_LABEL_KEYS = {
  granted: 'cancelReasonGranted',
  too_expensive: 'cancelReasonTooExpensive',
  unmet_need: 'cancelReasonUnmetNeed',
  other: 'cancelReasonOther'
};

export default function CancelReasonPopup({ onContinue, onSkip, onClose }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState(null);
  const [text, setText] = useState('');

  const handleContinue = () => {
    const answer = {};
    if (reason) answer.cancelReason = reason;
    const trimmed = text.trim();
    if (trimmed) answer.cancelReasonText = trimmed;
    onContinue(Object.keys(answer).length > 0 ? answer : null);
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-reason-title"
    >
      {/* Scrolls inside the viewport: on a 375px phone with the keyboard up,
          four options + the textarea are taller than what is left. */}
      <div className="modal max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2
              id="cancel-reason-title"
              className="font-display text-xl font-semibold text-text-primary"
            >
              {t('accountSubscription.cancelReasonTitle')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('accountSubscription.cancelReasonClose')}
              className="-mt-3 -mr-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          <fieldset className="space-y-2 mb-5">
            <legend className="sr-only">{t('accountSubscription.cancelReasonTitle')}</legend>
            {CANCEL_REASONS.map((value) => (
              <label
                key={value}
                className={`flex items-center gap-3 min-h-[48px] px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  reason === value ? 'border-sage bg-sage-50' : 'border-border-light hover:bg-neutral-50'
                }`}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  value={value}
                  checked={reason === value}
                  onChange={() => setReason(value)}
                  className="h-5 w-5 shrink-0 accent-[#6B8F72]"
                />
                <span className="text-text-primary">{t(`accountSubscription.${REASON_LABEL_KEYS[value]}`)}</span>
              </label>
            ))}
          </fieldset>

          <label htmlFor="cancel-reason-text" className="block text-sm font-medium text-text-primary mb-2">
            {t('accountSubscription.cancelReasonTextLabel')}
          </label>
          <textarea
            id="cancel-reason-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={REASON_TEXT_MAX}
            rows={3}
            className="w-full rounded-lg border border-border p-3 text-base text-text-primary focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-sage"
          />
          <p className="mt-1 text-sm text-text-muted text-right" aria-live="polite">
            {text.length > REASON_TEXT_COUNTER_FROM
              ? `${REASON_TEXT_MAX - text.length} ${t('accountSubscription.cancelReasonCharsLeft')}`
              : ''}
          </p>

          <div className="flex justify-end gap-3 mt-5">
            <Button variant="tertiary" size="md" onClick={onSkip} className="min-h-[48px]">
              {t('accountSubscription.cancelReasonSkip')}
            </Button>
            <Button variant="primary" size="md" onClick={handleContinue} className="min-h-[48px]">
              {t('accountSubscription.cancelReasonContinue')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
